import { supabase } from '../lib/supabaseClient';
import type { CostSnapshot, CreateRecipeLineInput, Recipe, RecipeLine, RecipeVersion, UUID } from '../types/domain';

/**
 * Recipe / HPP adapter (FASE 7, migration 0039). recipes/recipe_versions/
 * cost_snapshots are tenant-wide (no branch_id). Costing method is fixed by
 * the existing backend (LAST_PURCHASE_PRICE, cost_basis.method) — this
 * adapter only reads/triggers it, never computes or overrides a cost
 * client-side. Writes go through fn_create_recipe_version_checked /
 * fn_compute_recipe_cost_checked (migration 0039) — never a direct
 * `.insert()`.
 */

export async function listRecipes(): Promise<Recipe[]> {
  const { data, error } = await supabase
    .from('recipes')
    .select('recipe_id, tenant_id, menu_id, status, created_at')
    .order('created_at', { ascending: false });
  if (error) throw new Error(`listRecipes failed: ${error.message}`);
  return (data ?? []) as Recipe[];
}

export async function listRecipeVersions(recipeId: UUID): Promise<RecipeVersion[]> {
  const { data, error } = await supabase
    .from('recipe_versions')
    .select('recipe_version_id, tenant_id, recipe_id, version_no, status, effective_from, effective_to, yield_qty, created_at')
    .eq('recipe_id', recipeId)
    .order('version_no', { ascending: false });
  if (error) throw new Error(`listRecipeVersions failed: ${error.message}`);
  return (data ?? []) as RecipeVersion[];
}

export async function listRecipeLines(recipeVersionId: UUID): Promise<RecipeLine[]> {
  const { data, error } = await supabase
    .from('recipe_lines')
    .select('recipe_line_id, tenant_id, recipe_version_id, item_id, uom_id, qty_per_yield')
    .eq('recipe_version_id', recipeVersionId);
  if (error) throw new Error(`listRecipeLines failed: ${error.message}`);
  return (data ?? []) as RecipeLine[];
}

/** fn_get_effective_recipe_version — resolves the version that's ACTIVE as of a given date (default: today). STABLE, RLS-protected read, safe to call directly. */
export async function getEffectiveRecipeVersion(recipeId: UUID, asOfDate?: string): Promise<UUID | null> {
  const { data, error } = await supabase.rpc('fn_get_effective_recipe_version', {
    p_recipe_id: recipeId,
    p_as_of_date: asOfDate ?? new Date().toISOString().slice(0, 10),
  });
  if (error) throw new Error(`fn_get_effective_recipe_version failed: ${error.message}`);
  return (data as UUID | null) ?? null;
}

export async function listCostSnapshots(recipeVersionId: UUID): Promise<CostSnapshot[]> {
  const { data, error } = await supabase
    .from('cost_snapshots')
    .select('snapshot_id, tenant_id, recipe_version_id, computed_at, unit_cost, is_preliminary, cost_basis')
    .eq('recipe_version_id', recipeVersionId)
    .order('computed_at', { ascending: false });
  if (error) throw new Error(`listCostSnapshots failed: ${error.message}`);
  return (data ?? []) as CostSnapshot[];
}

/**
 * Calls fn_create_recipe_version_checked (migration 0039). Creates the
 * recipes shell row for the menu on first use, then a new ACTIVE
 * recipe_version — the previous ACTIVE version (if any) is automatically
 * superseded server-side, never edited in place.
 */
export async function createRecipeVersion(
  menuId: UUID,
  effectiveFrom: string,
  yieldQty: number,
  lines: CreateRecipeLineInput[]
): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_create_recipe_version_checked', {
    p_menu_id: menuId,
    p_effective_from: effectiveFrom,
    p_yield_qty: yieldQty,
    p_lines: lines,
  });
  if (error) throw new Error(`fn_create_recipe_version_checked failed: ${error.message}`);
  return data as UUID;
}

/** Calls fn_compute_recipe_cost_checked (migration 0039) — recomputes HPP from the current LAST_PURCHASE_PRICE cost basis and stores a new cost_snapshots row. */
export async function computeRecipeCost(recipeVersionId: UUID): Promise<UUID> {
  const { data, error } = await supabase.rpc('fn_compute_recipe_cost_checked', { p_recipe_version_id: recipeVersionId });
  if (error) throw new Error(`fn_compute_recipe_cost_checked failed: ${error.message}`);
  return data as UUID;
}
